import { useContext, useState } from 'react';
import { Button } from 'reactstrap';
import { savePageBreaksPreference } from '@features/custom-dashboard/utils/pageBreaksStorage';
import { DashboardRow, DashboardSection } from '@g17eco/molecules/dashboard';
import {
  GridDashboardItem,
  GridDashboardItemBase,
  GridDashboardItemCreate,
  GridDashboardTempItem,
  InsightDashboard,
  InsightDashboardItemType,
  InsightDashboardTempItemType,
  MetricStatusOption,
  TextSubType,
  DashboardItemFilters,
  HistoricalUtrs,
} from '@g17eco/types/insight-custom-dashboard';
import { TitleEditing } from './TitleEditing';
import IconButton from '@g17eco/molecules/button/IconButton';
import { DashboardSettings } from './dashboard-settings';
import { GridLayoutDashboard } from './GridLayoutDashboard';
import { Layout } from 'react-grid-layout';
import { useDebouncedCallback } from 'use-debounce';
import {
  calculateItemHeight,
  getDisplayDashboardItemType,
  getGridSize,
  hasSDGContributionItem,
  isMediaType,
  isSDGContributionType,
} from './utils';
import { UnsavedChangesWarningPrompt } from '@g17eco/atoms/UnsavedChangesWarningPrompt';
import { GridItemProps } from './GridItem';
import { SiteAlertColors } from '../../slice/siteAlertsSlice';
import { IndexedItem, replaceItemsWithIndexes } from '../../utils/array';
import { useSiteAlert } from '../../hooks/useSiteAlert';
import { CompanyProfile } from '@features/company-profile';
import { SafeInitiativeFields } from '../../types/initiative';
import { SdgChart } from '@features/impact-performance/SdgChart';
import { ScorecardResponse } from '../../types/scorecard';
import { DataPeriods } from '@utils/dataPeriods';
import { CustomDashboardToolbar } from '@features/custom-dashboard/action-toolbar/CustomDashboardToolbar';
import { CustomDashboardContext } from './context/CustomDashboardWrapper';
import { generateObjectId } from '@utils/object-id';
import { CancelConfirmationModal } from '@features/custom-dashboard/CancelConfirmationModal';
import { useToggle } from '@hooks/useToggle';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { SurveyTypeDropdownProps } from '@features/survey-type-dropdown/SurveyTypeDropdown';
import { Sidebar } from '@g17eco/molecules/sidebar';
import { DashboardItemEditor } from '@features/custom-dashboard/item-editor';
import { shouldRecalculateGrid } from '@features/custom-dashboard';
import { SharingButton } from './shared-dashboard/SharingButton';
import { EditingDashboardItem } from '@features/custom-dashboard/types';
import { getLatestMetricText } from '@features/custom-dashboard/utils/dashboard-utils';
import { useExportDashboard } from '@features/custom-dashboard/hooks/useExportDashboard';
import { Loader } from '@g17eco/atoms/loader';

const ALERT_TIMEOUT = 2000;

type Props = Pick<GridItemProps, 'handleOpenUtrvHistoryModal' | 'survey' | 'integrationsData' | 'dataFilters' | 'rootOrg'> &
  Partial<Pick<SurveyTypeDropdownProps, 'surveyType'>> & {
    dashboard: InsightDashboard;
    handleCancel: () => void;
    handleSave: (dashboard: Partial<InsightDashboard>, keepEditing?: boolean) => Promise<void>;
    handleDelete: () => void;
    initiative: SafeInitiativeFields;
    scorecard?: ScorecardResponse;
    availablePeriods: DataPeriods[];
    period: DataPeriods | undefined;
    editingItem: EditingDashboardItem | undefined;
    setEditingItem: React.Dispatch<React.SetStateAction<EditingDashboardItem | undefined>>;
    metricStatusOptions?: MetricStatusOption[];
  };

const getSidebarTitle = (item: EditingDashboardItem | undefined) => {
  if (!item) {
    return '';
  }

  const action = '_id' in item && item._id ? 'Edit' : 'Add';

  switch (item.type) {
    case InsightDashboardItemType.Table:
      return `${action} table`;
    case InsightDashboardTempItemType.BulkMedia:
    case InsightDashboardItemType.Media:
      return `${action} media`;
    case InsightDashboardItemType.Headline:
    case InsightDashboardItemType.Text:
      return `${action} text`;
    case InsightDashboardItemType.Space:
      return `${action} space`;
    default:
      return `${action} chart`;
  }
};

const getItemToCalculateGridSize = ({
  updatingItem,
  dashboard,
}: {
  updatingItem: GridDashboardItemCreate;
  dashboard: InsightDashboard;
}) => {
  const isMetricTextItem = 'subType' in updatingItem && updatingItem.subType === TextSubType.Metric;

  if (!isMetricTextItem) {
    return updatingItem;
  }

  return { ...updatingItem, text: getLatestMetricText({ utrsData: dashboard.utrsData, item: updatingItem }) };
};

export const CustomDashboardEditing = ({
  dashboard,
  handleCancel,
  handleSave,
  handleDelete,
  handleOpenUtrvHistoryModal,
  initiative,
  survey,
  availablePeriods,
  period,
  surveyType,
  editingItem,
  setEditingItem,
  metricStatusOptions,
  rootOrg,
  dataFilters,
}: Props) => {
  const initiativeId = initiative._id;
  const queryParams: DashboardItemFilters = {
    ...dashboard.filters,
    period,
    surveyType: surveyType ?? SurveyType.Default,
  };
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [dashboardChanges, setDashboardChanges] = useState(dashboard);

  const {
    getHistoricalUtrsByCodes,
    hideOptions,
    hideQuestionReference,
    readOnly,
    hideShareButton,
    components,
  } = useContext(CustomDashboardContext);
  const [isConfirmingCancel, toggleConfirmingCancelModal] = useToggle();

  const { addSiteAlert, addSiteError } = useSiteAlert();

  const {
    isLoading: isLoadingPageBreaks,
    dashboardRef,
    pageBreaks,
    toggleShowPageBreaks,
  } = useExportDashboard({
    dashboardTitle: dashboard.title,
    initiativeId: initiative._id,
    hideTitleFromExports: dashboard.filters.hideTitleFromExports?.enabled,
  });

  const closeEdit = () => {
    setEditingItem(undefined);
  };

  const handleChangeTitle = (title: InsightDashboard['title']) => {
    setDashboardChanges((dashboard) => ({ ...dashboard, title }));
    setIsEditingTitle(false);
  };

  const onLayoutChange = (layout: Layout[]) => {
    // Explicitly extract only what needed to track changes in the layout
    const layoutMap = new Map(
      layout.map(({ i, x, y, w, h, minH, minW, maxH, maxW, isResizable }) => [
        i,
        { x, y, w, h, minW, minH, maxW, maxH, isResizable },
      ])
    );
    setDashboardChanges((dashboard) => ({
      ...dashboard,
      items: dashboard.items.map((item) => ({ ...item, gridSize: layoutMap.get(item._id) ?? item.gridSize })),
    }));
  };

  const onItemRemove = (item: GridDashboardItem) => {
    setDashboardChanges((dashboard) => ({
      ...dashboard,
      items: dashboard.items.filter((gridItem) => gridItem._id !== item._id),
    }));
  };

  const handleClickEdit = (item: GridDashboardItem) => {
    setEditingItem(item);
  };

  const handleClickDuplicate = (item: GridDashboardItem) => {
    const { _id, ...rest } = item;
    setDashboardChanges((dashboard) => {
      const item = {
        _id: generateObjectId(),
        ...rest,
      } as GridDashboardItem;

      return {
        ...dashboard,
        items: [...dashboard.items, item],
      };
    });
    addSiteAlert({
      content: `${getDisplayDashboardItemType(rest.type)} duplicated`,
      timeout: ALERT_TIMEOUT,
      color: SiteAlertColors.Light,
    });
  };

  const onItemResize = useDebouncedCallback((newItemSize: Layout) => {
    const updated = dashboardChanges.items.map((gridItem) => {
      if (gridItem._id === newItemSize.i) {
        return { ...gridItem, gridSize: newItemSize };
      }
      return gridItem;
    });
    setDashboardChanges((dashboard) => ({ ...dashboard, items: updated }));
  }, 100);

  const handleToggleConfirmingCancelModal = () => {
    if (hasUnsavedChanges) {
      toggleConfirmingCancelModal();
      return;
    }

    handleCancel();
  };

  const saveAndCleanUp = () => {
    handleSave({
      items: dashboardChanges.items,
      title: dashboardChanges.title,
      utrsData: dashboardChanges.utrsData,
    }).then(() => {
      const showPageBreaks = pageBreaks.length > 0;
      savePageBreaksPreference(initiativeId, showPageBreaks);
      dashboardChanges.items.forEach((item) => {
        if (!isMediaType(item)) {
          return;
        }
        item.files?.forEach((file) => {
          if ('originalFile' in file && file.originalFile) {
            URL.revokeObjectURL(file.url);
          }
        });
      });
    });
  };

  const saveAndCancelBtn = (
    <>
      <Button onClick={handleToggleConfirmingCancelModal} color='transparent'>
        Cancel
      </Button>
      <Button
        color='primary'
        onClick={saveAndCleanUp}
        className='ms-2'
      >
        <i className='fa-light fa-floppy-disk me-2' />
        Save
      </Button>
    </>
  );

  const updateItemsData = async (updatingItems: GridDashboardItemCreate[]): Promise<HistoricalUtrs[] | undefined> => {
    const codesFromItems = new Set<string>();

    updatingItems.forEach((updatingItem) => {
      if (!('variables' in updatingItem) || !updatingItem.variables) {
        // skipping as no additional data to load
        return;
      }
      Object.values(updatingItem.variables).forEach((variable) => {
        // Skip Integration data.
        if (!variable.integrationCode) {
          codesFromItems.add(variable.code);
        }
      });
    });

    const existingCodes = dashboardChanges.utrsData.map((utrData) => utrData.utr.code);
    const codesToLoad = Array.from(codesFromItems).filter((code) => !existingCodes.includes(code));

    if (codesToLoad.length === 0) {
      return;
    }
    
    try {
      const result = await getHistoricalUtrsByCodes({ initiativeId, utrCodes: codesToLoad, queryParams });

      // Merge valueLists - add new ones that don't already exist
      const existingValueListIds = new Set(dashboardChanges.valueLists.map((vl) => vl._id));
      const newValueLists = result.valueLists.filter((vl) => !existingValueListIds.has(vl._id));

      setDashboardChanges((dashboard) => {
        return {
          ...dashboard,
          utrsData: [...dashboard.utrsData, ...result.utrsData],
          valueLists: [...dashboard.valueLists, ...newValueLists],
        };
      });
      return result.utrsData;
    } catch (e) {
      addSiteError(e.message);
    }
  };

  const updateItem = async (items: GridDashboardItemCreate[] | GridDashboardItemCreate) => {
    const updatingItems = !Array.isArray(items) ? [items] : items;

    const updatingUtrsData = await updateItemsData(updatingItems);
    const recalculateUtrsData = [...dashboardChanges.utrsData, ...(updatingUtrsData ?? [])];

    const newChanges: IndexedItem<GridDashboardItem>[] = [];

    updatingItems.forEach((updatingItem) => {
      if ('_id' in updatingItem && updatingItem._id) {
        const index = dashboardChanges.items.findIndex((item) => item._id === updatingItem._id);
        if (index < 0) {
          return;
        }
        const gridSize = dashboardChanges.items[index].gridSize;
        const itemHeights = shouldRecalculateGrid(updatingItem)
          ? calculateItemHeight({ item: updatingItem, width: gridSize.w, utrsData: recalculateUtrsData })
          : {};
        const item = {
          ...updatingItem,
          gridSize: { ...gridSize, ...itemHeights },
        };
        newChanges.push({ item, index });
        return;
      }
      const item = {
        _id: generateObjectId(),
        ...updatingItem,
        gridSize: getGridSize({
          updatingItem: getItemToCalculateGridSize({ updatingItem, dashboard }),
          items: dashboard.items,
          utrsData: recalculateUtrsData,
        }),
      } as GridDashboardItem;
      newChanges.push({ item });
    });

    setDashboardChanges((dashboard) => {
      return {
        ...dashboard,
        items: replaceItemsWithIndexes(dashboard.items, newChanges),
      };
    });

    const uniqueTypes = Array.from(new Set(updatingItems.map((item) => item.type)))
      .map(getDisplayDashboardItemType)
      .join(', ');

    addSiteAlert({
      content: `${uniqueTypes}${updatingItems.length === 0 ? ' is' : '(s) are'} added successfully`,
      timeout: ALERT_TIMEOUT,
      color: SiteAlertColors.Success,
    });
    setEditingItem(undefined);
  };

  const hasUnsavedChanges =
    dashboard.title !== dashboardChanges.title ||
    JSON.stringify(dashboard.items) !== JSON.stringify(dashboardChanges.items);

  const sidebarTitle = getSidebarTitle(editingItem);

  const showStaticSDGContributionChart =
    dashboard.filters.sdgContribution?.enabled && !hasSDGContributionItem(dashboard.items);

  const processSDGContributionSetting = (dashboard: Partial<InsightDashboard>) => {
    const { filters, items } = dashboard;
    const { filters: oldFilters, items: oldItems } = dashboardChanges;
    const isPreviousEnabled = Boolean(oldFilters?.sdgContribution?.enabled);
    const isNowSDGEnabled = Boolean(filters?.sdgContribution?.enabled);

    if (isPreviousEnabled === isNowSDGEnabled) {
      return;
    }

    const dashboardItems: GridDashboardItem[] = oldItems.filter((item) => !isSDGContributionType(item));
    const sdgContributionItem = items?.find((item) => isSDGContributionType(item));
    if (isNowSDGEnabled && sdgContributionItem) {
      dashboardItems.push(sdgContributionItem);
    }

    setDashboardChanges({
      ...dashboardChanges,
      filters: { ...oldFilters, sdgContribution: { enabled: isNowSDGEnabled } },
      items: dashboardItems,
    });
  };

  const onSaveSettings = (dashboard: Partial<InsightDashboard>, keepEditing?: boolean) => {
    processSDGContributionSetting(dashboard);
    handleSave(dashboard, keepEditing);
  };

  const onEditItem = (id: string) => {
    setEditingItem(dashboard.items.find((i) => i._id === id));
  };

  const openAddingModal = (item: GridDashboardItemBase | GridDashboardTempItem) => {
    setEditingItem(item);
  };

  return (
    <div className='pb-6'>
      {isLoadingPageBreaks ? <Loader /> : null}
      <DashboardRow>
        <div className='d-flex w-100 justify-content-end'>
          <div className='d-flex'>
            {saveAndCancelBtn}
            <DashboardSettings
              key={dashboard._id}
              handleSave={onSaveSettings}
              handleDelete={handleDelete}
              dashboard={dashboard}
              hideOptions={hideOptions}
              availablePeriods={availablePeriods}
              metricStatusOptions={metricStatusOptions}
              disabled
              components={components}
            />
            <Button color='secondary' disabled className='ms-2'>
              <i className='fa-light fa-file-arrow-down mr-2'></i>
              <span>Export dashboard</span>
            </Button>
            {hideShareButton ? null : <SharingButton dashboard={dashboard} disabled />}
          </div>
        </div>
      </DashboardRow>
      {dashboard.filters.initiativeInfo?.enabled ? <CompanyProfile initiative={initiative} readOnly={false} /> : null}
      <DashboardRow>
        <div className='d-flex align-items-center'>
          {isEditingTitle ? (
            <TitleEditing
              title={dashboardChanges.title}
              handleSave={handleChangeTitle}
              handleCancel={() => setIsEditingTitle(false)}
            />
          ) : (
            <>
              <h3 className='text-ThemeHeadingLight'>{dashboardChanges.title}</h3>
              <IconButton
                tooltip='Edit title'
                color='transparent'
                outline={false}
                onClick={() => setIsEditingTitle(true)}
                icon='fa-light fa-pencil'
                className='text-ThemeIconSecondary ml-2'
              />
            </>
          )}
        </div>
      </DashboardRow>
      {showStaticSDGContributionChart && dashboard.scorecard ? (
        <DashboardSection>
          <SdgChart initiativeId={initiativeId} scorecard={dashboard.scorecard} />
        </DashboardSection>
      ) : null}
      <GridLayoutDashboard
        isEditing
        hideQuestionReference={hideQuestionReference}
        gridItems={dashboardChanges.items}
        utrsData={dashboardChanges.utrsData}
        onLayoutChange={onLayoutChange}
        onItemResize={onItemResize}
        onItemRemove={onItemRemove}
        handleClickEdit={handleClickEdit}
        handleClickDuplicate={handleClickDuplicate}
        integrationsData={dashboardChanges.integrationsData}
        handleOpenUtrvHistoryModal={handleOpenUtrvHistoryModal}
        readOnly={readOnly || !dashboardChanges.filters.baselinesTargets?.enabled}
        initiativeId={initiativeId}
        initiativeIds={dashboardChanges.filters.initiativeIds}
        survey={survey}
        scorecard={dashboard.scorecard}
        dashboardRef={dashboardRef}
        pageBreaks={pageBreaks}
        onEditItem={onEditItem}
        initiative={initiative}
        rootOrg={rootOrg}
        dataFilters={dataFilters}
        valueLists={dashboardChanges.valueLists}
        surveyUnitConfigs={dashboardChanges.surveyUnitConfigs}
      />
      <CustomDashboardToolbar
        openAddingModal={openAddingModal}
        handleCancel={handleToggleConfirmingCancelModal}
        pageBreaks={pageBreaks}
        isLoadingPageBreaks={isLoadingPageBreaks}
        toggleShowHideBreakpoints={toggleShowPageBreaks}
        saveDashboardChanges={saveAndCleanUp}
        updateItem={updateItem}
      />
      <Sidebar
        header={sidebarTitle}
        isOpen={!!editingItem}
        toggle={closeEdit}
        direction='end'
        className='dashboard__editor-sidebar'
      >
        <DashboardItemEditor
          editingItem={editingItem}
          handleCancel={closeEdit}
          initiativeId={initiativeId}
          updateItem={updateItem}
          queryParams={queryParams}
          survey={survey}
          integrationsData={dashboardChanges.integrationsData}
        />
      </Sidebar>
      <UnsavedChangesWarningPrompt hasUnsavedChanges />
      {isConfirmingCancel ? (
        <CancelConfirmationModal toggle={toggleConfirmingCancelModal} handleCancel={handleCancel} />
      ) : null}
    </div>
  );
};
